import { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import { useSkin } from '@/store/SkinContext';
import { SKIN_ICON } from '@/components/AppShell';
import { CARD_DIMS, type Formato, type PosV, type PosH } from '@/components/AtletaCard';
import type { PodioRunner } from '@/lib/api';
import { pillColor } from '@/lib/podios/pillColors';

interface Props {
  competicion: string;
  categoria: string;
  fecha?: string;
  podio: PodioRunner[];
  formato: Formato;
  escala: number;
  /** Alcance del degradado de legibilidad (0-100): % del bloque de datos que
   *  cubre. 0 = sin degradado, 50 = media altura, 100 = todo el bloque. */
  degradado: number;
  /** Opacidad del degradado (0-100): 0 = invisible, 100 = totalmente opaco. */
  opacidad: number;
  /** Esquina vertical donde se ancla el bloque (deja libre el lado opuesto). */
  posicion: PosV;
  /** Lado horizontal del bloque (solo aplica en 16:9, que limita el ancho). */
  posH: PosH;
  branding: PosV;
  fotoSrc?: string;
  logoDataUrl?: string;
  logoV: PosV;
  logoH: PosH;
  logoEscala: number;
  /** Modo overlay para el vídeo: fondo transparente, sin foto. overlayPart
   *  elige 'datos' (scrim + podio) o 'logo' (solo el logo). */
  overlay?: boolean;
  overlayPart?: 'datos' | 'logo';
}

/** Card del podio (DOM, se rasteriza con html2canvas). Usa los tokens --ld-*
 *  de la skin activa. Sustituye al render Pillow para la IMAGEN. */
export const PodioCard = forwardRef<HTMLDivElement, Props>(function PodioCard(
  { competicion, categoria, fecha, podio, formato, escala, degradado, opacidad, posicion, posH, branding, fotoSrc, logoDataUrl, logoV, logoH, logoEscala, overlay = false, overlayPart },
  ref,
) {
  const { skin } = useSkin();
  const { w, h } = CARD_DIMS[formato];

  // ── Auto-ajuste: el bloque de datos NUNCA se sale de la tarjeta ─────────
  // (pedido de Riki 2026-07-08: "que el cuadro de resultados nunca exceda
  // los límites de la imagen"). Con escala alta o podios largos (etiquetas,
  // nombres a dos líneas) el bloque puede crecer más que la tarjeta y, como
  // el card es overflow:hidden, el texto se CORTA por el borde. Tras cada
  // render se mide el bloque y, si sobresale, se reduce la escala efectiva
  // (con reflow real: el texto se recompone, no se estruja con transform).
  // El slider de tamaño pasa a significar "como mucho": pide más de lo que
  // cabe y se queda en lo máximo que quepa. Converge en 1-2 pasadas (0.98
  // de margen) y el epsilon evita bucles de re-render.
  const bloqueRef = useRef<HTMLDivElement | null>(null);
  const [ajuste, setAjuste] = useState(1);
  useLayoutEffect(() => {
    setAjuste(1); // el contenido o la escala pedida cambian → se re-mide desde cero
  }, [escala, formato, posicion, posH, branding, podio, competicion, categoria, fecha]);
  useLayoutEffect(() => {
    const el = bloqueRef.current;
    if (!el) return;
    if (el.offsetHeight > h && ajuste > 0.35) {
      const objetivo = Math.max(0.35, (h / el.offsetHeight) * ajuste * 0.98);
      if (objetivo < ajuste - 0.005) setAjuste(objetivo);
    }
  });

  const fs = (px: number) => Math.round(px * escala * ajuste);
  const mutedOp = 0.7;
  const filas = podio.slice(0, 3);
  const colPuesto = (i: number) =>
    i === 0 ? 'var(--ld-accent)' : i === 1 ? 'var(--ld-text)' : 'var(--ld-text)';

  const brandRow = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        fontFamily: 'var(--ld-font-mono)',
        fontSize: fs(20),
        letterSpacing: '0.18em',
        color: 'var(--ld-text)',
      }}
    >
      <span style={{ opacity: mutedOp }}>mundoatletismo.es</span>
      <span style={{ flex: 1, height: 2, background: 'var(--ld-line-strong, var(--ld-line))' }} />
      <img src={SKIN_ICON[skin]} alt="" width={fs(36)} height={fs(36)} style={{ display: 'block' }} />
    </div>
  );

  const cabecera = (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div
          style={{
            fontFamily: 'var(--ld-font-display)',
            fontWeight: 800,
            fontSize: fs(40),
            lineHeight: 1.05,
            textTransform: 'uppercase',
            letterSpacing: '0.01em',
            flex: 1,
          }}
        >
          {competicion}
        </div>
        {fecha ? (
          <div
            style={{
              fontFamily: 'var(--ld-font-mono)',
              fontSize: fs(18),
              letterSpacing: '0.08em',
              color: 'var(--ld-text)',
              opacity: mutedOp,
              border: '2px solid var(--ld-line-strong, var(--ld-line))',
              borderRadius: 999,
              padding: `${fs(6)}px ${fs(14)}px`,
              whiteSpace: 'nowrap',
            }}
          >
            {fecha}
          </div>
        ) : null}
      </div>
      <div
        style={{
          fontFamily: 'var(--ld-font-mono)',
          fontSize: fs(20),
          letterSpacing: '0.12em',
          color: 'var(--ld-accent)',
          textTransform: 'uppercase',
          marginTop: fs(6),
        }}
      >
        {categoria}
      </div>
    </div>
  );

  const podium = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: fs(14) }}>
      {filas.map((p, i) => (
        <div key={p.puesto + p.nombre} style={{ display: 'flex', alignItems: 'flex-start', gap: fs(16) }}>
          <span style={{ fontFamily: 'var(--ld-font-display)', fontWeight: 800, fontSize: fs(58), lineHeight: 1, color: colPuesto(i), width: fs(50) }}>
            {p.puesto}
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: fs(2), flex: 1 }}>
            <span style={{ fontFamily: 'var(--ld-font-display)', fontWeight: 700, fontSize: fs(34), lineHeight: 1.05, textTransform: 'uppercase' }}>
              {p.nombre}
            </span>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <span style={{ fontFamily: 'var(--ld-font-mono)', fontSize: fs(17), letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ld-text)', opacity: mutedOp }}>
                {p.club_real || p.club}
              </span>
              {p.viento ? (
                <span style={{ fontFamily: 'var(--ld-font-mono)', fontSize: fs(16), color: 'var(--ld-text)', opacity: mutedOp, whiteSpace: 'nowrap' }}>
                  {p.viento}
                </span>
              ) : null}
            </div>
            {p.etiquetas?.length ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: fs(6), marginTop: fs(4) }}>
                {p.etiquetas.map((tag) => (
                  <span
                    key={tag}
                    style={{
                      fontFamily: 'var(--ld-font-mono)',
                      fontSize: fs(14),
                      fontWeight: 600,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      background: pillColor(tag).bg,
                      color: pillColor(tag).fg,
                      borderRadius: 999,
                      padding: `${fs(3)}px ${fs(10)}px`,
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
          <span style={{ fontFamily: 'var(--ld-font-display)', fontWeight: 800, fontSize: fs(46), lineHeight: 1, color: colPuesto(i), fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
            {p.marca}
          </span>
        </div>
      ))}
    </div>
  );

  const logoMax = Math.round(Math.min(w, h) * 0.16 * logoEscala);
  const LOGO_PAD = 10;
  const arriba = posicion === 'arriba';
  // En 16:9 el bloque ocupa como mucho el 50% y se ancla izquierda/derecha;
  // en 9:16 ocupa todo el ancho.
  const ancho169 = formato === '16:9';
  const izq = posH === 'izquierda';
  const showData = !overlay || overlayPart !== 'logo';
  const showLogo = (!overlay || overlayPart !== 'datos') && !!logoDataUrl;
  const showScrim = overlay ? showData : !!fotoSrc;
  // Degradado de legibilidad: capa interna al bloque (inset:0), así su alcance
  // es relativo al propio bloque de datos —no a la tarjeta— estén donde estén
  // y midan lo que midan. `degradado` (0-100) = % del bloque que cubre antes
  // de volverse transparente; `opacidad` (0-100) = opacidad de la capa.
  const scrimStyle = showScrim
    ? {
        position: 'absolute' as const,
        inset: 0,
        background: `linear-gradient(${arriba ? 'to bottom' : 'to top'}, var(--ld-bg) 0%, var(--ld-bg) 50%, transparent ${degradado}%)`,
        opacity: opacidad / 100,
        zIndex: 0,
      }
    : null;

  return (
    <div
      ref={ref}
      style={{
        width: w,
        height: h,
        position: 'relative',
        overflow: 'hidden',
        background: overlay
          ? 'transparent'
          : fotoSrc
            ? 'var(--ld-bg)'
            : 'linear-gradient(180deg, var(--ld-bg-grad-top) 0%, var(--ld-bg-grad-bot) 100%)',
        color: 'var(--ld-text)',
        fontFamily: 'var(--ld-font-sans)',
      }}
    >
      {fotoSrc && !overlay ? (
        <img src={fotoSrc} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : null}

      {/* Bloque compacto anclado arriba o abajo; el lado opuesto queda libre
          (p.ej. para el logo de cliente). El degradado va dentro del bloque
          para que su alcance sea relativo a los datos. */}
      {showData ? (
        <div
          ref={bloqueRef}
          style={{
            position: 'absolute',
            left: ancho169 ? (izq ? 0 : undefined) : 0,
            right: ancho169 ? (izq ? undefined : 0) : 0,
            maxWidth: ancho169 ? '50%' : undefined,
            top: arriba ? 0 : undefined,
            bottom: arriba ? undefined : 0,
            padding: 56,
          }}
        >
          {scrimStyle ? <div style={{ position: 'absolute', inset: 0, background: 'var(--ld-bg)', opacity: 0.3, zIndex: 0 }} /> : null}
          {scrimStyle ? <div style={scrimStyle} /> : null}
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: fs(14) }}>
            {branding === 'arriba' ? brandRow : null}
            {cabecera}
            {podium}
            {branding === 'abajo' ? brandRow : null}
          </div>
        </div>
      ) : null}

      {showLogo ? (
        <img
          src={logoDataUrl}
          alt=""
          style={{
            position: 'absolute',
            top: logoV === 'arriba' ? LOGO_PAD : undefined,
            bottom: logoV === 'arriba' ? undefined : LOGO_PAD,
            left: logoH === 'izquierda' ? LOGO_PAD : undefined,
            right: logoH === 'izquierda' ? undefined : LOGO_PAD,
            maxWidth: logoMax,
            maxHeight: logoMax,
            objectFit: 'contain',
          }}
        />
      ) : null}
    </div>
  );
});
